<template>
  <div class="container-fluid" id="surveyPage">
    <page-title
      :title="this.$t('survey')"
      iconClasses="fas fa-question"
      :isDataLoading="isDataLoading"
      :isTimelineDisplayed="true"
    />

    <!-- FORM CONTROLS -->
    <form-controls
      v-if="controlsConfig.length > 0"
      :controls="controlsConfig"
      v-on:updateControl="handleChange"
      v-on:setDefaultValues="setFormControlsDefault"
      :displayConfig="{ toggleableGroups: ['filters'] }"
    />

    <!-- SURVEY LABEL INFORMATIONS -->
    <div class="container-fluid mt-4 pt-2 mb-2 center-title" v-if="downloaded && dataset">
      <h5
        v-if="!isSmallScreen"
        v-html="
          $t('survey_infos', [
            dataset.total_answer_count,
            dataset.total_session_count,
            dataset.percent,
          ])
        "
      ></h5>
      <label
        v-if="isSmallScreen"
        v-html="
          $t('survey_infos', [
            dataset.total_answer_count,
            dataset.total_session_count,
            dataset.percent,
          ])
        "
      ></label>
    </div>

    <!-- SURVEY CHARTS GRID -->
    <div class="row" v-if="dataset && dataset.charts && dataset.charts.length > 0">
      <div
        :class="isContainerSurveyClass"
        v-for="(chart, i) in dataset.charts"
        :key="i"
        :data-id="i"
      >
        <div
          class="row pt-1 pb-2 chart"
          style="height: 100%"
          v-if="chart && chartToShow(chart.answer_type)"
        >
          <survey-chart
            :config="chart"
            :chartId="'surveyChart' + i"
            :site="Number(formControl.site)"
            :chartIndex="i + 1"
          ></survey-chart>
        </div>
      </div>
    </div>

    <!-- Bottom row, download button -->
    <div class="container-fluid" v-if="downloaded">
      <div class="row">
        <button
          class="btn btn-chart mb-3 mx-auto"
          @click="downloadSurveyAnswers"
          :disabled="isDataLoading"
        >
          {{ $t('download.survey') }}
          <i class="ml-2 fas fa-download"></i>
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import Vue from 'vue'
import moment from 'moment'
import { utilsMixin, chartMixin, sitesMixin } from '../utils/mixins'
import VueScrollTo from 'vue-scrollto'

//Scroll to
Vue.use(VueScrollTo, {
  container: 'body',
  duration: 500,
  easing: 'ease',
  offset: -100,
  force: true,
  cancelable: true,
  onStart: false,
  onDone: false,
  onCancel: false,
  x: false,
  y: true,
})

export default {
  name: 'Survey',
  dependencies: ['dataService', 'adminService'],
  mixins: [utilsMixin, chartMixin, sitesMixin],
  props: ['period'],
  data() {
    return {
      downloaded: false,
      dataset: {},
      timeout: null,
      formControl: {},
      controlsConfig: [],
    }
  },
  mounted() {
    this.adminService.getAllSitesWithSurvey().then(data => {
      let resultSites = data.result
      if (this.getCurrentUser.role == 'partner') {
        resultSites = resultSites.filter(site => {
          var found = this.getCurrentUser.sites.find(userSite => {
            return site.site === userSite.site
          })
          return found ? true : false
        })
        if (!resultSites || resultSites.length == 0) {
          this.$router.replace('dashboard')
        }
      }

      this.$store.dispatch('config/setCurrentSitesWithSurvey', resultSites).then(() => {
        this.formControl['site'] = this.checkDefaultSite(this.$route.params.site)
        this.controlsConfig = this.generateFormControlsConfig()
        this.$emit('updateSite', [this.formControl.site])

        this.loadSiteSurveyData()
      })
    })
  },
  beforeDestroy() {
    this.dataService.cancelDataRequest(this.currentRequestId)
  },
  watch: {
    period: {
      handler() {
        if (this.period) {
          this.loadSiteSurveyData()
        }
      },
      immediate: true,
    },
  },
  computed: {
    isContainerSurveyClass() {
      return {
        'container-fluid': true,
        'col-sm-5': !this.isSmallScreen,
        'mr-3': this.isSmallScreen,
        'ml-3': this.isSmallScreen,
        'mb-3': this.isSmallScreen,
        'mb-4': !this.isSmallScreen,
      }
    },
  },
  components: {
    'survey-chart': () => import('./charts/SurveyChart'),
    'page-title': () => import('./utils/PageTitle'),
    'form-controls': () => import('./utils/FormControls'),
  },
  methods: {
    handleChange(change) {
      this.formControl[change.vModel] = change.value
      if (change.vModel == 'site') {
        this.$emit('updateSite', this.formControl.site)
        this.controlsConfig = this.generateFormControlsConfig()
      }
      this.fetchSurveyAnswers()
    },
    loadSiteSurveyData() {
      if (!this.sitesWithSurvey || this.sitesWithSurvey.length == 0) {
        return
      }

      this.fetchSurveyAnswers()
    },
    fetchSurveyAnswers() {
      this.requestCount++

      this.start = moment(this.period[0], this.dateFormat).format('YYYY-MM-DD')
      this.end = moment(this.period[1], this.dateFormat).format('YYYY-MM-DD')

      VueScrollTo.scrollTo('#surveyPage')

      if (this.getCurrentUser.role == 'partner') {
        let found = this.getCurrentUser.sites.find(s => {
          return s.site == this.formControl.site
        })
        if (!found) {
          this.formControl.site = this.getCurrentUser.sites[0]
        }
      }

      window.history.pushState(
        null,
        null,
        '/survey/site/' +
          this.formControl.site +
          '/language/' +
          (this.formControl.language || 'all') +
          '/histopad_rating/' +
          (this.formControl.histopad_rating || 'all') +
          '/visit_rating/' +
          (this.formControl.visit_rating || 'all') +
          '/collected_treasure_count/' +
          (this.formControl.collected_treasure_count || 'all') +
          '/start/' +
          this.start +
          '/end/' +
          this.end,
      )

      this.dataService
        .getSurvey(this.formControl, this.start, this.end, this.currentRequestId)
        .then(data => {
          if (this.formControl.site === data.result.site_location) {
            let surveyDataset = data.result
            if (surveyDataset && surveyDataset.charts) {
              surveyDataset.charts = surveyDataset.charts.filter(chart => {
                return chart && chart.answer_type !== 'OpenAnswer'
              })
              this.dataset = surveyDataset
            } else {
              this.dataset = []
            }
            this.downloaded = true
            this.requestCount--
          }
        })
        .catch(e => {
          if (e.message != 'cancel') {
            console.error('Survey fetching error', e)
            this.dataLoadedError = true
          }
          this.requestCount--
        })

      this.currentRequestId = `survey${JSON.stringify(this.formControl)}${this.start}${this.end}`
    },
    downloadSurveyAnswers: function() {
      VueScrollTo.scrollTo('#surveyPage')
      this.requestCount++
      this.dataService
        .downloadSurvey(
          this.formControl,
          this.start,
          this.end,
          this.$locale,
          this.getCurrentUser.email,
          this.dataset.total_answer_count,
          this.dataset.max_question_index,
        )
        .then(data => {
          this.$log.debug('Data type', data.type)
          if (data.type == 'application/vnd.openxmlformats') {
            const url = window.URL.createObjectURL(new Blob([data]))
            const link = document.createElement('a')
            link.href = url
            link.setAttribute(
              'download',
              `export_survey_${moment().format('YYYY-MM-DD_HH:mm:ss')}.xls`,
            )
            document.body.appendChild(link)
            link.click()
          } else {
            this.showToast(this.$t('download.survey_email', [this.getCurrentUser.email]))
          }
          this.requestCount--
        })
        .catch(e => {
          console.error('Error', e)
          this.requestCount--
        })
    },
    chartToShow(answerType) {
      return answerType !== 'OpenAnswer'
    },
    generateFormControlsConfig() {
      let config = [
        {
          label: this.$t('site.title'),
          type: 'select',
          vModel: 'site',
          default: this.checkDefaultSite(this.$route.params.site),
          options: this.sitesWithSurvey.map(site => ({
            value: site.site,
            label: site.name,
          })),
        },
        {
          label: this.$t('filter.by_language'),
          type: 'select',
          vModel: 'language',
          default: this.$route.params.language ? this.$route.params.language : 'all',
          group: 'filters',
          options: [
            { value: 'all', label: this.$t('select_all') },
            ...this.getConfigFiltersFromSite(this.formControl.site).language.map(lang => ({
              value: lang,
              label: this.$t(`language.${lang}`),
            })),
          ],
        },
        {
          label: this.$t('filter.by_histopad_rating'),
          type: 'select',
          vModel: 'histopad_rating',
          default: this.$route.params.histopad_rating ? this.$route.params.histopad_rating : 'all',
          group: 'filters',
          options: [
            { value: 'all', label: this.$t('select_all') },
            { value: '0', label: '0' },
            { value: '1', label: '1' },
            { value: '2', label: '2' },
            { value: '3', label: '3' },
            { value: '4', label: '4' },
            { value: '5', label: '5' },
          ],
        },
        {
          label: this.$t('filter.by_visit_rating'),
          type: 'select',
          vModel: 'visit_rating',
          default: this.$route.params.visit_rating ? this.$route.params.visit_rating : 'all',
          group: 'filters',
          options: [
            { value: 'all', label: this.$t('select_all') },
            { value: '0', label: '0' },
            { value: '1', label: '1' },
            { value: '2', label: '2' },
            { value: '3', label: '3' },
            { value: '4', label: '4' },
            { value: '5', label: '5' },
          ],
        },
        {
          label: this.$t('filter.collected_treasure_count'),
          type: 'select',
          vModel: 'collected_treasure_count',
          default: this.$route.params.collected_treasure_count
            ? this.$route.params.collected_treasure_count
            : 'all',
          group: 'filters',
          options: [
            { value: 'all', label: this.$t('select_all') },
            ...this.getConfigFiltersFromSite(
              this.formControl.site,
            ).collected_treasure_count.map(treasure => ({ value: treasure, label: treasure })),
          ],
        },
      ]

      return config
    },
    checkDefaultSite(site) {
      let defaultSite
      if (site) {
        let found = this.sitesWithSurvey.find(sws => {
          return String(sws.site) === site
        })
        if (found) {
          defaultSite = site
        } else {
          defaultSite = String(this.sitesWithSurvey[0].site)
        }
      } else {
        defaultSite = String(this.sitesWithSurvey[0].site)
      }

      return defaultSite
    },
  },
}
</script>

<style>
.table > tbody > tr > td {
  vertical-align: middle;
}

.table > tbody > tr > th {
  vertical-align: middle;
}

@media (min-width: 576px) {
  .col-sm-5 {
    flex: 1 0 46.666667% !important;
    max-width: 46.666667% !important;
  }
}
</style>
